// The mentions inbox (OPEN-23): unread @mentions of the current user, until read.
// Refreshes when the live channel reports a mention ('jabberr:mention') and after any
// seen() call ('jabberr:seen'). The count drives the bell badge and the tab title;
// the list drives the banner across the top of the main area.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MentionItem } from '@jabberr/core';
import { api } from '../lib/api.js';
import { notifyMention } from '../lib/notify.js';

export interface MentionsState {
  mentions: MentionItem[];
  count: number;
  dismissed: MentionItem | null;
  /** Hides the banner for the session only; the badge and the count stay. */
  dismiss: () => void;
  refresh: () => void;
}

export function useMentions(userId: string | null | undefined, openConversationId?: string): MentionsState {
  const [mentions, setMentions] = useState<MentionItem[]>([]);
  const [count, setCount] = useState(0);
  const [dismissed, setDismissed] = useState<MentionItem | null>(null);
  /** Mentions already announced with a browser notification this session. */
  const announced = useRef<Set<string>>(new Set());
  /** The newest mention when the hook mounted (the initial load must not spam notifications). */
  const loadedOnce = useRef(false);

  const load = useCallback(async (): Promise<void> => {
    if (!userId) return;
    try {
      const res = await api.mentions.list();
      setMentions(res.mentions);
      setCount(res.count);
      // Notify for the newest mention that appeared since the last look, only when the page
      // is hidden or the thread is not the one open right now.
      const latest = res.mentions[0];
      if (
        loadedOnce.current &&
        latest &&
        !announced.current.has(latest.mentionedAt) &&
        (document.hidden || latest.conversationId !== openConversationId)
      ) {
        announced.current.add(latest.mentionedAt);
        const who = latest.byName ?? 'Someone';
        const where = latest.conversationTitle ?? latest.projectName;
        notifyMention(`${who} mentioned you`, `${where}: ${latest.excerpt}`);
      }
      loadedOnce.current = true;
    } catch {
      // Unavailable is a first-class state; the badge and banner just keep what they had.
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    void load();
    const onMention = (): void => {
      void load();
    };
    const onSeen = (): void => {
      void load();
    };
    window.addEventListener('jabberr:mention', onMention);
    window.addEventListener('jabberr:seen', onSeen);
    return () => {
      window.removeEventListener('jabberr:mention', onMention);
      window.removeEventListener('jabberr:seen', onSeen);
    };
  }, [userId, openConversationId, load]);

  const dismiss = useCallback((): void => {
    setDismissed(mentions[0] ?? null);
  }, [mentions]);

  return { mentions, count, dismissed, dismiss, refresh: load };
}
